import * as React from 'react';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import Draggable from 'react-draggable';

// Make sure a click on the dialog's close (X) button, or anywhere inside the
// scrollable content, never starts a drag.
export const DRAGGABLE_PAPER_CANCEL_SELECTOR =
  '[class*="MuiDialogContent-root"], [aria-label="close"], button';

/**
 * Root cause this works around: react-draggable's DraggableCore calls
 * e.preventDefault() on `touchstart` for any touch that lands on the drag
 * `handle` (unless `disabled`). The dialog's title is the handle, and the
 * close (X) IconButton lives inside that title (see BootstrapDialogTitle),
 * so on a real touchscreen the preventDefault suppresses the synthetic click
 * React would otherwise fire — the X never closes the dialog on phones.
 * Desktop mouse drags never call preventDefault on touchstart, so they were
 * never affected.
 *
 * Fix: disable dragging on small screens / coarse (touch) pointers, so the
 * handle never intercepts the touch in the first place. Desktop drag-by-title
 * with a mouse is unchanged.
 */
export function useDraggableDisabled() {
  // A literal query string (rather than the `(theme) => theme.breakpoints...`
  // callback form) avoids depending on a ThemeProvider being present, which
  // keeps this safe to use from components rendered without one (tests).
  // 599.95px matches MUI's default 'sm' breakpoint (600px) via `down('sm')`.
  const isSmallScreen = useMediaQuery('(max-width:599.95px)');
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  return isSmallScreen || isCoarsePointer;
}

/**
 * Factory for a MUI <Dialog PaperComponent> that is draggable by its title
 * on desktop mouse input, and plain (non-draggable) on phones/touch so the
 * close button keeps working. `handleId` must match the `id` on the
 * dialog's title element (e.g. BootstrapDialogTitle's `id` / the Dialog's
 * `aria-labelledby`), without the leading '#'.
 */
export function createDraggablePaperComponent(handleId, options = {}) {
  const { cancel = DRAGGABLE_PAPER_CANCEL_SELECTOR } = options;

  function DraggablePaperComponent(props) {
    const disabled = useDraggableDisabled();
    return (
      <Draggable handle={`#${handleId}`} cancel={cancel} disabled={disabled}>
        <Paper {...props} />
      </Draggable>
    );
  }

  return DraggablePaperComponent;
}

export default createDraggablePaperComponent;
